import { assert } from '@fe/core';
import { FeMapPlugin } from '@fe/maplibre-extensions';
import * as turf from '@turf/turf';
import { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import { EventBus, EventBusSubscription } from '../../app/contexts/event-bus';
import { store } from '../../app/state/store';
import { drawingObjectToGeoJson, isDrawingObjectActive } from '../helpers/helpers';
import { FeDraw } from './drawing/map-draw';
import {
    DrawingObject,
    DrawingObjectCategory,
    DrawingObjectCenterStyle,
    DrawingObjectCircleGeometry,
    DrawingObjectGeometry,
    DrawingObjectLabelAlignment,
    DrawingObjectLayerSettings,
    DrawingObjectLineType,
    DrawingObjectRectangleGeometry,
    DrawingObjectType,
    DrawingObjectUpdateDiff
} from './types';

const SOURCE_OBJECTS = 'fe-drawing-objects';
const SOURCE_CENTROIDS = 'fe-drawing-objects-centroids';

export interface DrawingObjectProperties {
    id: string;
    fillColor: string;
    lineWidth: number;
    lineColor: string;
    lineStyle: DrawingObjectLineType;
    minZoom: number;
    labelMinZoom: number;
    label: string;
    labelColor: string;
    showLabel: boolean;
    isGrayedOut: boolean;
}

export interface DrawingObjectCentroidProperties {
    id: string;
    label: string;
    labelColor: string;
    labelMinZoom: number;
    labelAlignment: DrawingObjectLabelAlignment;
    centerStyle: DrawingObjectCenterStyle;
    centerColor: string;
    centerSize: number;
    centerMinZoom: number;
}

function generateHatchPattern(color: string) {
    const svg = `
        <svg width="10" height="10" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,0 L10,10" stroke="${color}" stroke-width="2"/>
        </svg>
    `;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    return URL.createObjectURL(blob);
}

export class DrawingObjectsLayer implements FeMapPlugin {
    static readonly CONTEXT_MENU_LAYER = SOURCE_OBJECTS + '-fill';
    private updateSubscription: EventBusSubscription | null = null;
    get name() {
        return SOURCE_OBJECTS;
    }

    private map: MaplibreMap | null = null;
    private settings: DrawingObjectLayerSettings = {
        show: false,
        avoidance: false,
        alert: false,
        view: false,
        viewObjectIds: []
    };
    private editObject: DrawingObject | null = null;
    private readonly GRAYEDOUT_INTERVAL_SECONDS = 60;
    private readonly GRAYED_OUT_COLOR = '#808080';
    private timeCheckInterval: number | null = null;
    private currentDate: Date = new Date();
    private readonly _drawLayer: FeDraw = new FeDraw();
    private visibleObjects: string[] = [];

    get drawLayer() {
        return this._drawLayer;
    }

    async onAdd(map: MaplibreMap) {
        assert(!this.map);

        this._drawLayer.start(map);
        this.map = map;
        // TODO: make a sprite along with the center point styles STL?
        //const image = await map.loadImage(hatchPng);
        //map.addImage('hatch', image.data);

        const slot = 'fe-drawing-objects-slot';

        map.addSource(SOURCE_OBJECTS, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
        });

        map.addSource(SOURCE_CENTROIDS, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
        });

        map.addLayer(
            {
                id: SOURCE_OBJECTS + '-fill',
                source: SOURCE_OBJECTS,
                filter: ['>=', ['zoom'], ['get', 'minZoom']],
                type: 'fill',
                paint: {
                    //'fill-pattern': 'hatch'
                    'fill-color': ['case', ['==', ['get', 'isGrayedOut'], true], this.GRAYED_OUT_COLOR, ['to-color', ['get', 'fillColor']]],
                    'fill-opacity': [
                        'case',
                        ['==', ['get', 'isGrayedOut'], true],
                        0.6, // Grayed out opacity
                        1.0 // Normal opacity
                    ]
                }
            },
            slot
        );

        map.addLayer(
            {
                filter: ['all', ['==', ['get', 'lineStyle'], 0], ['>=', ['zoom'], ['get', 'minZoom']]],
                id: SOURCE_OBJECTS + '-line-solid',
                source: SOURCE_OBJECTS,
                type: 'line',
                layout: {
                    'line-join': 'round'
                },
                paint: {
                    'line-color': ['to-color', ['get', 'lineColor'], '#00f'],
                    'line-width': ['get', 'lineWidth']
                }
            },
            slot
        );

        map.addLayer(
            {
                filter: ['all', ['==', ['get', 'lineStyle'], 1], ['>=', ['zoom'], ['get', 'minZoom']]],
                id: SOURCE_OBJECTS + '-line-dash',
                source: SOURCE_OBJECTS,
                type: 'line',
                layout: {},
                paint: {
                    'line-color': ['to-color', ['get', 'lineColor'], '#00f'],
                    'line-width': ['get', 'lineWidth'],
                    'line-dasharray': [3, 3]
                }
            },
            slot
        );

        map.addLayer(
            {
                filter: ['all', ['==', ['get', 'lineStyle'], 2], ['>=', ['zoom'], ['get', 'minZoom']]],
                id: SOURCE_OBJECTS + '-line-dotted',
                source: SOURCE_OBJECTS,
                type: 'line',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': ['to-color', ['get', 'lineColor'], '#00f'],
                    'line-width': ['get', 'lineWidth'],
                    'line-dasharray': [0.5, 2]
                }
            },
            slot
        );

        map.addLayer(
            {
                id: SOURCE_CENTROIDS + '-indicator',
                source: SOURCE_CENTROIDS,
                type: 'symbol',
                filter: ['>=', ['zoom'], ['get', 'centerMinZoom']],
                layout: {
                    'symbol-placement': 'point',
                    'icon-anchor': 'center',
                    'icon-image': [
                        'case',
                        ['==', ['get', 'centerStyle'], 1],
                        'fe:circle-filled',
                        ['==', ['get', 'centerStyle'], 2],
                        'fe:plus',
                        ['==', ['get', 'centerStyle'], 3],
                        'fe:x',
                        ['==', ['get', 'centerStyle'], 4],
                        'fe:circle-hollow',
                        ['==', ['get', 'centerStyle'], 5],
                        'fe:circle-filled',
                        ''
                    ],
                    'icon-size': ['/', ['get', 'centerSize'], 10],
                    'icon-allow-overlap': true,
                    'icon-pitch-alignment': 'map'
                },
                paint: {
                    'icon-color': ['get', 'centerColor']
                }
            },
            slot
        );

        map.addLayer(
            {
                id: SOURCE_OBJECTS + '-label-perimeter',
                source: SOURCE_OBJECTS,
                filter: ['all', ['>=', ['zoom'], ['get', 'labelMinZoom']], ['==', ['boolean', ['get', 'showLabel'], false], true]],
                type: 'symbol',
                layout: {
                    'symbol-placement': 'line',
                    'text-field': ['get', 'label'],
                    'text-font': ['Open Sans Regular'],
                    'text-anchor': 'bottom',
                    'text-allow-overlap': true
                },
                paint: {
                    'text-color': ['get', 'labelColor']
                }
            },
            slot
        );

        map.addLayer(
            {
                id: SOURCE_CENTROIDS + '-label',
                source: SOURCE_CENTROIDS,
                filter: ['all', ['>=', ['zoom'], ['get', 'labelMinZoom']], ['!=', ['get', 'labelAlignment'], 3]],
                type: 'symbol',
                layout: {
                    'symbol-placement': 'point',
                    'text-field': ['get', 'label'],
                    'text-font': ['Open Sans Regular'],
                    'text-offset': [0, 1],
                    'text-anchor': [
                        'case',
                        ['==', ['get', 'labelAlignment'], 0],
                        'right',
                        ['==', ['get', 'labelAlignment'], 1],
                        'center',
                        ['==', ['get', 'labelAlignment'], 2],
                        'left',
                        'center'
                    ],
                    'text-allow-overlap': true
                },
                paint: {
                    'text-color': ['get', 'labelColor']
                }
            },
            slot
        );

        this.onObjectsUpdated({
            updated: [],
            deleted: [],
            added: store.getState().drawingObjects.objects,
            objectsToDisableFpmSync: []
        });
        this.updateSubscription = EventBus.on('drawing-objects-updated', this.onObjectsUpdated.bind(this));
        this.startTimeBasedChecking();
    }

    onRemove() {
        assert(this.map);
        this.editObject = null;
        this._drawLayer?.finalize();
        this.updateSubscription?.unsubscribe();
        this.updateSubscription = null;
        if (this.timeCheckInterval) {
            self.clearInterval(this.timeCheckInterval);
            this.timeCheckInterval = null;
        }
        this.map = null;
    }
    private startTimeBasedChecking() {
        this.timeCheckInterval = self.setInterval(() => {
            this.updateTimeBasedGraying();
        }, this.GRAYEDOUT_INTERVAL_SECONDS * 1000);

        // Run initial check
        this.updateTimeBasedGraying();
    }

    private updateTimeBasedGraying() {
        assert(this.map);
        const currentDate = new Date();
        this.currentDate = currentDate;
        const sourceObjects = <GeoJSONSource>this.map.getSource(SOURCE_OBJECTS);

        const objects = store.getState().drawingObjects.objects;
        const updates: Array<{
            id: string;
            addOrUpdateProperties: Array<{ key: string; value: any }>;
        }> = [];

        for (const object of objects) {
            if (!this.visibleObjects.includes(object.id)) {
                continue;
            }

            const shouldGray = shouldGrayOutObject(object, currentDate);
            updates.push({
                id: object.id,
                addOrUpdateProperties: [{ key: 'isGrayedOut', value: shouldGray }]
            });
        }

        if (updates.length > 0) {
            sourceObjects.updateData({ update: updates });
        }
    }

    private onObjectsUpdated = (diff: DrawingObjectUpdateDiff) => {
        assert(this.map);
        const sourceObjects = <GeoJSONSource>this.map.getSource(SOURCE_OBJECTS);
        const sourceCenters = <GeoJSONSource>this.map.getSource(SOURCE_CENTROIDS);
        const objectAdd = [];
        const centerAdd = [];
        const toDelete: string[] = diff.deleted.filter((x) => x !== this.editObject?.id); // Don't delete currently edit object

        this.visibleObjects = this.visibleObjects.filter((x) => !diff.deleted.includes(x));

        for (const object of diff.added) {
            if (!this.matchesSettings(object)) {
                continue;
            }
            this.visibleObjects.push(object.id);
            const feature = toFeature(object, this.currentDate);
            objectAdd.push(feature);
            centerAdd.push(toCenterFeature(feature, object));
        }

        const objectUpdate: GeoJSON.Feature[] = [];
        const centerUpdate: GeoJSON.Feature[] = [];
        for (const object of diff.updated) {
            // Don't modify currently edited object
            if (object.id === this.editObject?.id) {
                continue;
            }

            if (!this.matchesSettings(object)) {
                if (this.visibleObjects.includes(object.id)) {
                    toDelete.push(object.id);
                }
                continue;
            }
            const feature = toFeature(object, this.currentDate);
            objectUpdate.push(feature);
            centerUpdate.push(toCenterFeature(feature, object));
        }

        sourceObjects.updateData({
            add: objectAdd,
            update: objectUpdate.map((x) => ({
                id: x.id as string,
                newGeometry: x.geometry,
                addOrUpdateProperties: Object.entries(x.properties!).map((e) => ({ key: e[0], value: e[1] }))
            })),
            remove: toDelete
        });

        sourceCenters.updateData({
            add: centerAdd,
            update: centerUpdate.map((x) => ({
                id: x.id as string,
                newGeometry: x.geometry,
                addOrUpdateProperties: Object.entries(x.properties!).map((e) => ({ key: e[0], value: e[1] }))
            })),
            remove: toDelete
        });
    };

    updateSettings(settings: DrawingObjectLayerSettings) {
        this.settings = settings;
        const objects = store.getState().drawingObjects.objects;
        const toDelete: string[] = [];
        const toAdd: DrawingObject[] = [];
        for (const object of objects) {
            if (this.matchesSettings(object)) {
                if (!this.visibleObjects.includes(object.id)) {
                    toAdd.push(object);
                }
            } else {
                toDelete.push(object.id);
            }
        }

        this.onObjectsUpdated({
            added: toAdd,
            deleted: toDelete,
            updated: [],
            objectsToDisableFpmSync: []
        });
    }

    beginEdit(object: DrawingObject) {
        assert(!this.editObject);
        this.editObject = object;
        this.add(object);
    }

    graphicEditStart() {
        assert(this.editObject);

        // Hide object
        this.delete(this.editObject.id);

        // Setup draw
        this.drawLayer.edit(this.editObject.type, this.editObject.geometry);
    }

    graphicEditApply() {
        assert(this.editObject);
        const newGeometry = this.drawLayer.getEdited();
        this.editObject.geometry = newGeometry;
        this.add(this.editObject);
        this.drawLayer.setMode(null);
        return newGeometry;
    }

    graphicEditCancel() {
        assert(this.editObject);
        this.drawLayer.setMode(null);
        this.add(this.editObject);
        this.drawLayer.setMode(null);
    }

    endEdit() {
        if (!this.editObject) {
            return;
        }
        this.drawLayer.setMode(null);
        this.delete(this.editObject.id);
        // Get latest copy
        const objects = store.getState().drawingObjects.objects;
        const replacementObject = objects.find((x) => x.id === this.editObject!.id);
        if (replacementObject && this.matchesSettings(replacementObject)) {
            this.add(replacementObject);
        }
        this.editObject = null;
    }

    updatePropertyObject<T extends keyof DrawingObjectProperties>(id: string, key: T, value: DrawingObjectProperties[T]) {
        assert(this.map);
        (<GeoJSONSource>this.map.getSource(SOURCE_OBJECTS)).updateData({
            update: [
                {
                    id,
                    addOrUpdateProperties: [{ key, value }]
                }
            ]
        });
    }

    updatePropertyCenter<T extends keyof DrawingObjectCentroidProperties>(id: string, key: T, value: DrawingObjectCentroidProperties[T]) {
        assert(this.map);
        (<GeoJSONSource>this.map.getSource(SOURCE_CENTROIDS)).updateData({
            update: [
                {
                    id,
                    addOrUpdateProperties: [{ key, value }]
                }
            ]
        });
    }

    updateGeometry(id: string, type: DrawingObjectType, geometry: DrawingObjectGeometry) {
        assert(this.map);
        // Patch the edit object as well
        if (this.editObject?.id === id) {
            this.editObject.geometry = geometry;
        }

        const newGeometry = drawingObjectToGeoJson(type, geometry).geometry;
        (<GeoJSONSource>this.map.getSource(SOURCE_OBJECTS)).updateData({
            update: [
                {
                    id: id,
                    newGeometry: newGeometry
                }
            ]
        });

        let center: GeoJSON.Point;
        if (type === DrawingObjectType.polygon) {
            center = turf.centroid(newGeometry).geometry;
        } else {
            const g = geometry as DrawingObjectRectangleGeometry | DrawingObjectCircleGeometry;
            center = turf.point([g.centerX, g.centerY]).geometry;
        }

        (<GeoJSONSource>this.map.getSource(SOURCE_CENTROIDS)).updateData({
            update: [
                {
                    id: id,
                    newGeometry: center
                }
            ]
        });
    }

    private add(object: DrawingObject) {
        assert(this.map);
        const feature = toFeature(object, this.currentDate);
        (<GeoJSONSource>this.map.getSource(SOURCE_OBJECTS)).updateData({
            add: [feature]
        });

        (<GeoJSONSource>this.map.getSource(SOURCE_CENTROIDS)).updateData({
            add: [toCenterFeature(feature, object)]
        });
    }

    private delete(id: string) {
        assert(this.map);
        (<GeoJSONSource>this.map.getSource(SOURCE_OBJECTS)).updateData({
            remove: [id]
        });

        (<GeoJSONSource>this.map.getSource(SOURCE_CENTROIDS)).updateData({
            remove: [id]
        });
    }

    private matchesSettings(object: DrawingObject): boolean {
        if (!this.settings.show) {
            return false;
        }

        if (object.category === DrawingObjectCategory.alertBased) {
            return this.settings.alert;
        }

        if (object.category === DrawingObjectCategory.avoidance) {
            return this.settings.avoidance;
        }

        if (!this.settings.view) {
            return false;
        }

        return this.settings.viewObjectIds.includes(object.id);
    }
}

function toFeature(object: DrawingObject, currentDate: Date): GeoJSON.Feature {
    return {
        id: object.id,
        type: 'Feature',
        geometry: drawingObjectToGeoJson(object.type, object.geometry).geometry,
        properties: getPropertiesObject(object, currentDate)
    };
}

function toCenterFeature(feature: GeoJSON.Feature, object: DrawingObject): GeoJSON.Feature<GeoJSON.Point, DrawingObjectCentroidProperties> {
    return {
        id: object.id,
        type: 'Feature',
        geometry: (object.type === DrawingObjectType.polygon
            ? turf.centroid(feature)
            : turf.point([object.geometry.centerX, object.geometry.centerY])
        ).geometry,
        properties: getPropertiesCenter(object)
    };
}
function shouldGrayOutObject(object: DrawingObject, originalCurrentDate: Date): boolean {
    if (!object.validTime?.greyOutNonActive) {
        return false;
    }

    return !isDrawingObjectActive(object, originalCurrentDate);
}
function getPropertiesObject(object: DrawingObject, currentDate: Date): DrawingObjectProperties {
    return {
        id: object.id,
        fillColor: object.fill.color,
        lineWidth: object.line.width,
        lineColor: object.line.color,
        lineStyle: object.line.style,
        minZoom: object.zoom.object,
        labelMinZoom: object.zoom.label,
        label: object.label.text,
        labelColor: object.label.color,
        showLabel: object.label.alignment === DrawingObjectLabelAlignment.perimeter,
        isGrayedOut: shouldGrayOutObject(object, currentDate)
    };
}

function getPropertiesCenter(object: DrawingObject): DrawingObjectCentroidProperties {
    return {
        id: object.id,
        label: object.label.text,
        labelColor: object.label.color,
        labelAlignment: object.label.alignment,
        labelMinZoom: object.zoom.label,
        centerStyle: object.center.style,
        centerSize: object.center.size,
        centerColor: object.center.color,
        centerMinZoom: object.zoom.point
    };
}
